import { useQuery } from '@tanstack/react-query';
import { pmsFrom } from '@/integrations/pms';

export interface SectionVisibility {
  show_hero: boolean;
  show_services: boolean;
  show_segments: boolean;
  show_why_choose_us: boolean;
  show_process: boolean;
  show_testimonials: boolean;
  show_offers: boolean;
  show_about: boolean;
  show_service_areas: boolean;
  show_faq: boolean;
  show_contact_form: boolean;
  show_featured_projects: boolean;
  show_brands: boolean;
  show_maintenance_plans: boolean;
  show_jobs: boolean;
  show_team: boolean;
  show_shop: boolean;
}

const defaultVisibility: SectionVisibility = {
  show_hero: true,
  show_services: true,
  show_segments: true,
  show_why_choose_us: true,
  show_process: true,
  show_testimonials: true,
  show_offers: true,
  show_about: true,
  show_service_areas: true,
  show_faq: true,
  show_contact_form: true,
  show_featured_projects: true,
  show_brands: true,
  show_maintenance_plans: true,
  show_jobs: true,
  show_team: true,
  show_shop: true,
};

const defaultSectionOrder = [
  'hero', 'segments', 'services', 'why_choose_us', 'process', 'featured_projects', 'brands', 'shop', 'testimonials',
  'offers', 'about', 'service_areas', 'maintenance_plans', 'jobs', 'team', 'faq', 'contact_form',
];

export const useSectionVisibility = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['site-content-visibility'],
    queryFn: async () => {
      const { data, error } = await pmsFrom('site_content')
        .select('show_hero, show_services, show_segments, show_why_choose_us, show_process, show_testimonials, show_offers, show_about, show_service_areas, show_faq, show_contact_form, show_featured_projects, show_brands, show_maintenance_plans, show_jobs, show_team, section_order')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching visibility settings:', error);
        return { visibility: defaultVisibility, sectionOrder: defaultSectionOrder };
      }
      
      if (data) {
        return {
          visibility: {
            show_hero: data.show_hero ?? true,
            show_services: data.show_services ?? true,
            show_segments: (data as any).show_segments ?? true,
            show_why_choose_us: data.show_why_choose_us ?? true,
            show_process: data.show_process ?? true,
            show_testimonials: data.show_testimonials ?? true,
            show_offers: data.show_offers ?? true,
            show_about: data.show_about ?? true,
            show_service_areas: data.show_service_areas ?? true,
            show_faq: data.show_faq ?? true,
            show_contact_form: data.show_contact_form ?? true,
            show_featured_projects: (data as any).show_featured_projects ?? true,
            show_brands: (data as any).show_brands ?? true,
            show_maintenance_plans: (data as any).show_maintenance_plans ?? true,
            show_jobs: (data as any).show_jobs ?? true,
            show_team: (data as any).show_team ?? true,
            show_shop: (data as any).show_shop ?? true,
          } as SectionVisibility,
          sectionOrder: Array.isArray((data as any).section_order) 
            ? (data as any).section_order as string[] 
            : defaultSectionOrder,
        };
      }
      
      return { visibility: defaultVisibility, sectionOrder: defaultSectionOrder };
    },
    staleTime: 1000 * 30,
  });

  return {
    visibility: data?.visibility || defaultVisibility,
    sectionOrder: data?.sectionOrder || defaultSectionOrder,
    isLoading,
  };
};
